import { createContext, useContext, useEffect, useState, useCallback } from "react";

/*
  Theme system — three user-selectable modes, persisted per device:
    • "light" (default)  — bright XEA UI 24/7
    • "dark"             — dark UI 24/7
    • "auto"             — dark at night (19:00–07:00 local), light in the day
*/

const STORAGE_KEY = "xea-theme";
const ThemeContext = createContext(null);

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
};

const isNightNow = () => {
  const h = new Date().getHours();
  return h >= 19 || h < 7;
};

const computeIsDark = (mode) => mode === "dark" || (mode === "auto" && isNightNow());

export const ThemeProvider = ({ children }) => {
  const [mode, setModeState] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return ["light", "dark", "auto"].includes(saved) ? saved : "light";
  });
  const [isDark, setIsDark] = useState(() => computeIsDark(mode));

  const apply = useCallback((m) => {
    const dark = computeIsDark(m);
    document.documentElement.classList.toggle("dark", dark);
    setIsDark(dark);
  }, []);

  const setMode = useCallback((m) => {
    localStorage.setItem(STORAGE_KEY, m);
    setModeState(m);
    apply(m);
  }, [apply]);

  // Apply on mount + whenever mode changes
  useEffect(() => {
    apply(mode);
  }, [mode, apply]);

  // In auto mode, re-evaluate day/night every minute
  useEffect(() => {
    if (mode !== "auto") return;
    const id = setInterval(() => apply("auto"), 60 * 1000);
    return () => clearInterval(id);
  }, [mode, apply]);

  return (
    <ThemeContext.Provider value={{ mode, setMode, isDark }}>
      {children}
    </ThemeContext.Provider>
  );
};
